const prefixName = 'reorderQuestionByStepName:';

/**
 * Validates the reorder inputs.
 * @param {Element|null} $stepToMoveElem - step element to move, or null if not found
 * @param {Element|null} $anchorStepElem - anchor step element, or null if not found
 * @param {string} position - expected to be 'before' or 'after'
 * @returns {string|null} warning message (without prefix) if invalid; null if valid
 */
function getValidationError($stepToMoveElem, $anchorStepElem, position) {
	if (position !== 'before' && position !== 'after') {
		return 'position must be "before" or "after"';
	}

	if (!$stepToMoveElem || !$anchorStepElem) {
		return 'stepToMove or anchorStepName not found';
	}

	if ($stepToMoveElem === $anchorStepElem) {
		return 'stepToMove and anchorStepName must be different steps';
	}

	if ($stepToMoveElem.parentNode !== $anchorStepElem.parentNode) {
		return 'stepToMove and anchorStepName must be in the same parent';
	}

	return null;
}

/**
 * Moves a question step before or after another step and syncs step IDs.
 * Works before modForm init (pass formElement), since modForm reads the step order from the DOM on init.
 * If modForm is already initialized on the same form, modForm steps are synced too. The form swiper is updated if it exists.
 * Intended to be called before the user starts navigating the form.
 * @param {string} stepToMove - data-step-name of the step to move
 * @param {string} anchorStepName - data-step-name of the anchor step
 * @param {'before' | 'after'} [position='before'] - where to place stepToMove relative to the anchor
 * @param {HTMLFormElement} [formElement] - form containing the steps; defaults to window.modForm.opts.form
 * @returns {boolean} true if the step ends up in the requested position; false otherwise
 * @example
 * reorderQuestionByStepName('ProjectType', 'ZipCode', 'after', document.querySelector('#form'));
 */
export default function reorderQuestionByStepName(stepToMove, anchorStepName, position = 'before', formElement) {
	const modFormOpts = window.modForm?.opts;
	const form = formElement || modFormOpts?.form;

	if (!form) {
		console.warn(`${prefixName} no formElement provided and modForm.opts.form is not defined`);
		return false;
	}

	const stepsSelector = modFormOpts?.stepsSelector || '[data-bind="step"]';
	const $stepToMoveElem = form.querySelector(`[data-step-name="${CSS.escape(stepToMove)}"]`);
	const $anchorStepElem = form.querySelector(`[data-step-name="${CSS.escape(anchorStepName)}"]`);

	const validationError = getValidationError($stepToMoveElem, $anchorStepElem, position);
	if (validationError) {
		console.warn(`${prefixName} ${validationError}`);
		return false;
	}

	const isBefore = position === 'before';
	const currentSibling = isBefore ? $anchorStepElem.previousElementSibling : $anchorStepElem.nextElementSibling;

	if (currentSibling === $stepToMoveElem) {
		return true;
	}

	$anchorStepElem.parentNode.insertBefore($stepToMoveElem, isBefore ? $anchorStepElem : $anchorStepElem.nextElementSibling);

	const steps = form.querySelectorAll(stepsSelector);

	steps.forEach((step, index) => {
		step.id = `step${index + 1}`;
	});

	const isModFormInitialized = Boolean(modFormOpts?.steps) && modFormOpts.form === form;

	if (isModFormInitialized) {
		modFormOpts.steps = steps;
	}

	// The site may create the swiper before modForm init, so fall back to the instance Swiper stores on its container
	const swiperInstance = (isModFormInitialized && modFormOpts.formSwiperInstance) || $anchorStepElem.parentNode.parentNode?.swiper;
	swiperInstance?.update?.();

	return true;
}
